import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import DriverSelector from '../../components/driverSelector';
import { Form, Alert, Spinner, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import axios from 'axios';

const GenerateInvoice = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthsData, setMonthsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [existingInvoices, setExistingInvoices] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [editableInvoices, setEditableInvoices] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(0);

  const months = [
    { value: '01', label: t('january') },
    { value: '02', label: t('february') },
    { value: '03', label: t('march') },
    { value: '04', label: t('april') },
    { value: '05', label: t('may') },
    { value: '06', label: t('june') },
    { value: '07', label: t('july') },
    { value: '08', label: t('august') },
    { value: '09', label: t('september') },
    { value: '10', label: t('october') },
    { value: '11', label: t('november') },
    { value: '12', label: t('december') }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Завантаження списку водіїв
  const fetchDrivers = async () => {
    try {
      const response = await axios.get("/api/users", { 
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } 
      });
      const formattedDrivers = response.data.map((driver) => ({
        value: driver.id,
        label: `${driver.firstName} ${driver.lastName}`
      }));
      setDrivers(formattedDrivers);
      return formattedDrivers;
    } catch (err) {
      console.error("Помилка завантаження списку водіїв:", err);
      return [];
    }
  };

  // Завантаження даних тижнів з сервера
  const fetchMonthsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get('/api/monthly-reports/months', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMonthsData(response.data.data);
    } catch (err) {
      console.error('Помилка завантаження даних тижнів:', err);
      setError('Помилка завантаження даних тижнів');
    } finally {
      setLoading(false);
    }
  };

  // Отримання тижнів для обраного місяця та року
  const getWeeksForSelectedMonth = () => {
    if (!monthsData || !selectedMonth || !selectedYear) {
      return [];
    }
    
    const yearData = monthsData[selectedYear.toString()];
    if (!yearData) {
      return [];
    }
    
    return yearData[selectedMonth] || [];
  };

  // Завантаження даних при монтуванні компонента
  useEffect(() => {
    const initializeData = async () => {
      // Завантажуємо дані тижнів
    fetchMonthsData();
      
      // Завантажуємо список водіїв
      const loadedDrivers = await fetchDrivers();
    
    // Отримуємо параметри з URL
    const driverId = searchParams.get('driverId');
    const month = searchParams.get('month');
    const year = searchParams.get('year');
    
      if (driverId && month && year && loadedDrivers.length > 0) {
        // Знаходимо водія зі списку
        const driver = loadedDrivers.find(d => d.value === parseInt(driverId));
        
        if (driver) {
          setSelectedDriver(driver);
      setSelectedMonth(month);
      setSelectedYear(parseInt(year));
      
          // Автоматично завантажуємо дані для попереднього перегляду
          await fetchDriverReports(driver, month, parseInt(year));
        }
      }
    };
    
    initializeData();
  }, [searchParams]);

  // Валідація полів
  const validateFields = () => {
    if (!selectedDriver) {
      setValidationError('Будь ласка, виберіть водія');
      return false;
    }
    if (!selectedMonth) {
      setValidationError('Будь ласка, виберіть місяць');
      return false;
    }
    if (!selectedYear) {
      setValidationError('Будь ласка, виберіть рік');
      return false;
    }
    setValidationError(null);
    return true;
  };

  // Завантаження існуючих рахунків
  const fetchExistingInvoices = async (driver = null, month = null, year = null) => {
    const driverToUse = driver || selectedDriver;
    const monthToUse = month || selectedMonth;
    const yearToUse = year || selectedYear;

    if (!driverToUse || !monthToUse || !yearToUse) {
      console.log('❌ [FETCH EXISTING INVOICES] Відсутні параметри для запиту');
      return;
    }

    try {
      const url = `/api/invoices/check/${driverToUse.value}/${monthToUse}/${yearToUse}`;
      const response = await axios.get(url);
      
      setExistingInvoices(response.data);
    } catch (error) {
      console.error('❌ [FETCH EXISTING INVOICES] Помилка завантаження існуючих рахунків:', error);
      setExistingInvoices(null);
    }
  };

  // Завантаження даних водія з таблиці invoices
  const fetchDriverReports = async (driver = null, month = null, year = null) => {
    // Використовуємо передані параметри або беремо зі стейту
    const driverToUse = driver || selectedDriver;
    const monthToUse = month || selectedMonth;
    const yearToUse = year || selectedYear;

    if (!driverToUse || !monthToUse || !yearToUse) {
      setValidationError('Будь ласка, виберіть водія, місяць та рік');
      return;
    }

    setLoadingReports(true);
    setError(null);
    setReportsData(null);
    setValidationError(null);

    try {
    const driverIdParamRaw = (driverToUse && typeof driverToUse === 'object')
        ? driverToUse.value
        : driverToUse;
      const driverIdParam = parseInt(driverIdParamRaw, 10);
      if (!Number.isFinite(driverIdParam)) {
        setValidationError('Некоректний водій: не вдалося визначити driverId');
        setLoadingReports(false);
        return;
      }
      
      const response = await axios.get(`/api/invoices/driver/${driverIdParam}/data`, {
        params: {
          month: monthToUse,
          year: yearToUse
        }
      });
      
      const data = response.data;
      // Зберігаємо як reportsData агреговані дані по invoices для поточного вигляду
      setReportsData({
        success: true,
        reportCount: data.count,
        totals: { totalPayout: data.totalAmount },
      });
      setExistingInvoices({ count: data.count, invoices: data.invoices });
      
      // Ініціалізуємо редагувані дані
      setTimeout(() => {
        if (data.invoices) {
          const editable = data.invoices.map(invoice => {
            let weeks = invoice.weeks;
            if (typeof weeks === 'string') {
              try {
                weeks = JSON.parse(weeks);
              } catch {
                weeks = [];
              }
            }
            
            // Отримуємо опис з invoice_data або використовуємо дефолтний
            let invoiceData = invoice.invoice_data;
            if (typeof invoiceData === 'string') {
              try {
                invoiceData = JSON.parse(invoiceData);
              } catch {
                invoiceData = {};
              }
            }
            
            return {
              ...invoice,
              weeks: Array.isArray(weeks) ? weeks.map(w => ({ ...w })) : [],
              description: invoiceData?.description || 'Wynajem samochodu'
            };
          });
          setEditableInvoices(editable);
        }
      }, 100);

      // Підтягнути HTML попереднього перегляду з шаблону на сервері
      const rawHtml = await fetchPreviewHTMLFromServer(driverIdParam, monthToUse, yearToUse);
      console.log(`📄 [RAW HTML] Length: ${rawHtml?.length || 0}, Invoices count: ${data.invoices?.length || 0}`);
      const suffix = `/${monthToUse}/${yearToUse}`;
      const addNumberSuffix = (inputHtml) => (
        typeof inputHtml === 'string'
          ? inputHtml.replace(/(RACHUNEK\s+NR\s*[^<\n]+)/g, (m) => (m.includes(suffix) ? m : m + suffix))
          : ''
      );

      const formatPln = (amount) => {
        const num = Number(amount) || 0;
        return `${num.toFixed(2).replace('.', ',')} zł`;
      };

      // Функція для пропису числа польською мовою
      const numberToPolishWords = (amount) => {
        const num = Number(amount) || 0;
        const zloty = Math.floor(num);
        const grosze = Math.round((num - zloty) * 100);

        const ones = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć'];
        const teens = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
        const tens = ['', '', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
        const hundreds = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset'];
        const thousands = ['tysiąc', 'tysiące', 'tysięcy'];

        const convertLessThanThousand = (n) => {
          if (n === 0) return '';
          
          let result = '';
          const h = Math.floor(n / 100);
          const t = Math.floor((n % 100) / 10);
          const o = n % 10;

          if (h > 0) result += hundreds[h] + ' ';
          
          if (t === 1) {
            result += teens[o] + ' ';
          } else {
            if (t > 0) result += tens[t] + ' ';
            if (o > 0) result += ones[o] + ' ';
          }
          
          return result.trim();
        };

        const getZlotyWord = (n) => {
          if (n === 1) return 'złoty';
          const lastDigit = n % 10;
          const lastTwoDigits = n % 100;
          if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'złotych';
          if (lastDigit >= 2 && lastDigit <= 4) return 'złote';
          return 'złotych';
        };

        const getGroszeWord = (n) => {
          if (n === 1) return 'grosz';
          const lastDigit = n % 10;
          const lastTwoDigits = n % 100;
          if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'groszy';
          if (lastDigit >= 2 && lastDigit <= 4) return 'grosze';
          return 'groszy';
        };

        const getThousandsWord = (n) => {
          if (n === 1) return thousands[0];
          const lastDigit = n % 10;
          const lastTwoDigits = n % 100;
          if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return thousands[2];
          if (lastDigit >= 2 && lastDigit <= 4) return thousands[1];
          return thousands[2];
        };

        let result = '';

        if (zloty === 0) {
          result = 'zero złotych';
        } else {
          const thousandsNum = Math.floor(zloty / 1000);
          const remainder = zloty % 1000;

          if (thousandsNum > 0) {
            result += convertLessThanThousand(thousandsNum) + ' ' + getThousandsWord(thousandsNum) + ' ';
          }

          if (remainder > 0) {
            result += convertLessThanThousand(remainder) + ' ';
          }

          result += getZlotyWord(zloty);
        }

        if (grosze > 0) {
          result += ' ' + convertLessThanThousand(grosze) + ' ' + getGroszeWord(grosze);
        }

        return result.trim();
      };

      const getLastDayOfMonthStr = (y, m) => {
        const yearNum = parseInt(y, 10);
        const monthNum = parseInt(m, 10); // 1-12
        const lastDay = new Date(yearNum, monthNum, 0).getDate();
        const dd = String(lastDay).padStart(2, '0');
        const mm = String(monthNum).padStart(2, '0');
        return `${dd}.${mm}.${yearNum}`;
      };

      const rebuildBodyRows = (inputHtml) => {
        if (typeof inputHtml !== 'string' || !Array.isArray(data?.invoices) || data.invoices.length === 0) {
          return inputHtml || '';
        }
        const separator = '<div style="page-break-before: always;"></div>';
        const parts = inputHtml.split(separator).filter(p => p.trim().length > 0);
        
        // Якщо кількість частин не відповідає кількості рахунків, використовуємо кількість рахунків
        const numInvoices = data.invoices.length;
        const numParts = parts.length;
        
        console.log(`📄 [REBUILD BODY] Invoices: ${numInvoices}, HTML parts: ${numParts}`);
        
        // Обробляємо кожен рахунок
        const updated = data.invoices.map((inv, idx) => {
          // Якщо є відповідна частина HTML, використовуємо її, інакше беремо першу або останню
          let partHtml = parts[idx] || parts[0] || '';
          
          // 1) Пробуємо взяти з колонки weeks (масив {week, amount, type})
          let weeksArr = inv?.weeks;
          if (typeof weeksArr === 'string') {
            try { weeksArr = JSON.parse(weeksArr); } catch { weeksArr = null; }
          }
          // 2) Якщо weeks немає, беремо з invoice_data.services (якщо є)
          let invData = inv?.invoice_data;
          if (typeof invData === 'string') {
            try { invData = JSON.parse(invData); } catch { invData = {}; }
          }
          let services = null;
          const description = invData?.description || 'Wynajem samochodu';
          if (Array.isArray(weeksArr) && weeksArr.length > 0) {
            services = weeksArr.map(w => ({ name: description, unit: 'szt', quantity: 1, unitPrice: Number(w.amount || 0), total: Number(w.amount || 0) }));
          } else if (Array.isArray(invData?.services)) {
            services = invData.services;
          }
          if (!services || services.length === 0) return partHtml; // нічого не змінюємо
          const rows = services.map((s, i) => {
            const unit = s.unit || 'szt';
            const qty = s.quantity ?? 1;
            const price = Number(s.unitPrice ?? s.total ?? s.amount ?? 0);
            const total = Number(s.total ?? s.unitPrice ?? s.amount ?? 0);
            const name = s.name || 'Wynajem samochodu';
            return `\n                <tr>\n                  <td class="number-col">${i + 1}</td>\n                  <td class="name-col">${name}</td>\n                  <td class="unit-col">${unit}</td>\n                  <td class="quantity-col">${qty}</td>\n                  <td class="price-col">${formatPln(price).replace(' zł',' zł')}</td>\n                  <td class="total-col">${formatPln(total).replace(' zł',' zł')}</td>\n                </tr>`;
          }).join('');
          const sum = services.reduce((acc, s) => acc + Number(s.total ?? s.unitPrice ?? s.amount ?? 0), 0);
          
          // Зберігаємо обчислену суму в даних інвойсу для подальшого використання
          if (data.invoices[idx]) {
            data.invoices[idx].calculatedTotal = sum;
          }
          
          const newTbody = `<tbody>${rows}\n                <tr class="total-row">\n                  <td colspan="5" class="price-col">Razem</td>\n                  <td class="total-col">${formatPln(sum)}</td>\n                </tr>\n              </tbody>`;
          return partHtml.replace(/<tbody>[\s\S]*?<\/tbody>/, newTbody);
        });
        return updated.join(separator);
      };

      const replaceDueDate = (inputHtml) => {
        const due = getLastDayOfMonthStr(yearToUse, monthToUse);
        return typeof inputHtml === 'string'
          ? inputHtml.replace(/(<p><strong>Termin zapłaty:<\/strong>\s*)(.*?)(<\/p>)/g, `$1${due}$3`)
          : '';
      };

      const replacePaymentFields = (inputHtml) => {
        if (typeof inputHtml !== 'string') return '';
        
        const separator = '<div style="page-break-before: always;"></div>';
        const parts = inputHtml.split(separator).filter(p => p.trim().length > 0);
        
        // Обробляємо кожен рахунок з data.invoices
        const updated = data.invoices.map((inv, idx) => {
          // Якщо є відповідна частина HTML, використовуємо її, інакше беремо першу або останню
          let partHtml = parts[idx] || parts[0] || '';
          if (!inv) return partHtml;
          
          // Використовуємо обчислену суму з таблиці, якщо є, інакше обчислюємо самостійно
          let totalAmount = inv.calculatedTotal;
          
          if (!totalAmount) {
            // Якщо обчислена сума відсутня, обчислюємо самостійно
            let weeksArr = inv?.weeks;
            if (typeof weeksArr === 'string') {
              try { weeksArr = JSON.parse(weeksArr); } catch { weeksArr = null; }
            }
            
            let invData = inv?.invoice_data;
            if (typeof invData === 'string') {
              try { invData = JSON.parse(invData); } catch { invData = {}; }
            }
            
            if (Array.isArray(weeksArr) && weeksArr.length > 0) {
              totalAmount = weeksArr.reduce((sum, w) => sum + Number(w.amount || 0), 0);
            } else if (Array.isArray(invData?.services)) {
              totalAmount = invData.services.reduce((sum, s) => sum + Number(s.total ?? s.unitPrice ?? s.amount ?? 0), 0);
            } else {
              totalAmount = Number(inv.amount || 0);
            }
          }
          
          const formattedAmount = formatPln(totalAmount);
          const amountInWords = numberToPolishWords(totalAmount);
          
          console.log(`💰 [PAYMENT FIELDS] Invoice ${idx}: totalAmount=${totalAmount}, amountInWords="${amountInWords}"`);
          
          let result = partHtml;
          
          // Замінюємо {{amount_in_words}}
          result = result.replace(/\{\{amount_in_words\}\}/g, amountInWords);
          
          // Замінюємо всі {{total_amount}} на відформатовану суму
          result = result.replace(/\{\{total_amount\}\}/g, formattedAmount);
          
          return result;
        });
        
        return updated.join(separator);
      };

      const htmlWithNumber = addNumberSuffix(rawHtml);
      const withRows = rebuildBodyRows(htmlWithNumber);
      const withDueDate = replaceDueDate(withRows);
      const removeTemplateIfs = (inputHtml) => (typeof inputHtml === 'string'
        ? inputHtml.replace(/\{\{#if\s+bank_account\}\}/g, '').replace(/\{\{\/if\}\}/g, '')
        : '');
      const stripBankForCash = (inputHtml) => {
        const sep = '<div style="page-break-before: always;"></div>';
        const parts = (typeof inputHtml === 'string' ? inputHtml : '').split(sep).filter(p => p.trim().length > 0);
        
        // Обробляємо кожен рахунок з data.invoices
        const updated = data.invoices.map((inv, idx) => {
          // Якщо є відповідна частина HTML, використовуємо її, інакше беремо першу або останню
          let partHtml = parts[idx] || parts[0] || '';
          if (inv?.payment_method === 'cash') {
            const bankRe = new RegExp('<p><strong>Bank:\\s*<\\/strong>[^<]*<\\/p>', 'g');
            const accountRe = new RegExp('<p><strong>Numer(?: konto| konta):\\s*<\\/strong>[^<]*<\\/p>', 'g');
            return partHtml.replace(bankRe, '').replace(accountRe, '');
          }
          return partHtml;
        });
        return updated.join(sep);
      };
      const withoutIfs = removeTemplateIfs(withDueDate);
      const withBankStripped = stripBankForCash(withoutIfs);
      const finalHtml = replacePaymentFields(withBankStripped);
      console.log(`📄 [FINAL HTML] Length: ${finalHtml?.length || 0}, Separators count: ${(finalHtml.match(/<div style="page-break-before: always;"><\/div>/g) || []).length}`);
      setInvoicePreview({ invoice_number: data?.invoices?.[0]?.invoice_number || '', pdf_buffer: null, html: finalHtml });
      
    } catch (err) {
      console.error('❌ [CLIENT] Помилка завантаження даних:', err);
      setError('Помилка завантаження даних водія');
    } finally {
      setLoadingReports(false);
    }
  };

  // Збереження рахунку та завантаження PDF
  const saveAndDownloadInvoice = async () => {
    if (!invoicePreview?.html || !selectedDriver || !selectedMonth || !selectedYear) return;
    
    try {
      setGeneratingInvoice(true);
      const invoiceNumber = invoicePreview?.invoice_number || `${selectedMonth}-${selectedYear.toString().slice(-2)}`;
      const driverName = selectedDriver?.label || 'Driver';
      const fileName = `${invoiceNumber}/${selectedMonth} - ${driverName}.pdf`;
      
      // Спочатку зберігаємо рахунок зі статусом "saved"
      const saveResponse = await axios.post('/api/invoices/save', {
        driverId: selectedDriver.value,
        month: selectedMonth,
        year: selectedYear,
        status: 'saved'
      });

      if (saveResponse.data.success) {
        // Потім генеруємо та завантажуємо PDF
        const pdfResponse = await axios.post('/api/invoices/generate-from-html', {
          html: invoicePreview.html,
          fileName: fileName
        }, { responseType: 'arraybuffer' });

        const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        // Оновлюємо дані про існуючі рахунки
        await fetchExistingInvoices();
      }
    } catch (e) {
      console.error('Помилка збереження та генерації PDF:', e);
      setError('Помилка збереження рахунку або генерації PDF');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Оновлення значення тижня в редагуваних даних
  const updateWeekAmount = (invoiceIndex, weekIndex, newAmount) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex] && updated[invoiceIndex].weeks[weekIndex]) {
      updated[invoiceIndex].weeks[weekIndex].amount = parseFloat(newAmount) || 0;
      setEditableInvoices(updated);
    }
  };

  // Оновлення опису рахунку
  const updateInvoiceDescription = (invoiceIndex, description) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex]) {
      updated[invoiceIndex].description = description;
      setEditableInvoices(updated);
    }
  };

  // Оновлення номера рахунку
  const updateInvoiceNumber = (invoiceIndex, invoiceNumber) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex]) {
      updated[invoiceIndex].invoice_number = invoiceNumber;
      setEditableInvoices(updated);
    }
  };

  // Завантаження останнього номера рахунку
  const fetchLastInvoiceNumber = async () => {
    if (!selectedMonth || !selectedYear) return;
    
    try {
      const response = await axios.get(`/api/invoices/last-number/${selectedMonth}/${selectedYear}`);
      setLastInvoiceNumber(response.data.lastInvoiceNumber || 0);
    } catch (error) {
      console.error('Помилка отримання останнього номера рахунку:', error);
      setLastInvoiceNumber(0);
    }
  };

  // Оновлення тижня в рядку
  const updateWeekField = (invoiceIndex, weekIndex, field, value) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex] && updated[invoiceIndex].weeks[weekIndex]) {
      updated[invoiceIndex].weeks[weekIndex][field] = value;
      setEditableInvoices(updated);
    }
  };

  // Додавання нового рядка до рахунку
  const addWeekRow = (invoiceIndex) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex]) {
      if (!updated[invoiceIndex].weeks) {
        updated[invoiceIndex].weeks = [];
      }
      updated[invoiceIndex].weeks.push({
        week: '',
        amount: 0,
        type: updated[invoiceIndex].payment_method === 'transfer' ? 'payout' : 'cash'
      });
      setEditableInvoices(updated);
    }
  };

  // Видалення рядка з рахунку
  const removeWeekRow = (invoiceIndex, weekIndex) => {
    if (!editableInvoices) return;
    
    const updated = [...editableInvoices];
    if (updated[invoiceIndex] && updated[invoiceIndex].weeks) {
      updated[invoiceIndex].weeks = updated[invoiceIndex].weeks.filter((_, idx) => idx !== weekIndex);
      setEditableInvoices(updated);
    }
  };

  // Збереження змін у рахунках
  const saveInvoiceEdits = async () => {
    if (!editableInvoices || !selectedDriver) return;
    
    setSavingEdits(true);
    try {
      // Отримуємо дані водія
      const driverResponse = await axios.get(`/api/users/${selectedDriver.value}`);
      const driverData = driverResponse.data;
      
      // Оновлюємо кожен рахунок
      for (const invoice of editableInvoices) {
        const invoiceData = {
          invoice_number: invoice.invoice_number,
          driver_id: selectedDriver.value,
          month: selectedMonth,
          year: selectedYear,
          weeks: JSON.stringify(invoice.weeks),
          seller_data: invoice.seller_data || JSON.stringify({
            name: `${driverData.firstName} ${driverData.lastName}`,
            address: `${driverData.street || ''} ${driverData.house_number || ''}`.trim(),
            city: `${driverData.index || ''}, ${driverData.city || ''}`.replace(/^,\s*/, ''),
            pesel: driverData.pesel || ''
          }),
          buyer_data: invoice.buyer_data || JSON.stringify({
            name: "TORI Viktoriya Manachynska",
            address: "ul. Księcia Witolda 49/15",
            city: "50-202 Wrocław",
            nip: "7822599564"
          }),
          invoice_data: JSON.stringify({
            total: invoice.weeks.reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0),
            description: invoice.description || 'Wynajem samochodu'
          }),
          payment_method: invoice.payment_method,
          payment_due_date: invoice.payment_due_date || new Date().toISOString().split('T')[0],
          bank_account: driverData.account_number || '',
          status: invoice.status || 'draft'
        };
        
        await axios.put('/api/invoices/upsert', invoiceData);
      }
      
      alert('✅ Зміни успішно збережено!');
      setShowEditModal(false);
      
      // Перезавантажуємо дані
      await fetchDriverReports();
    } catch (error) {
      console.error('Помилка збереження змін:', error);
      alert('❌ Помилка збереження змін: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingEdits(false);
    }
  };

  // Отримати HTML попереднього перегляду з серверного шаблону

  const fetchPreviewHTMLFromServer = async (driverId, month, year) => {
    try {
      const { data } = await axios.get('/api/invoices/html', {
        params: { driver_id: driverId, month, year }
      });
      if (data && data.success) {
        return data.html || '';
      }
      return '';
    } catch (e) {
      console.error('❌ [CLIENT] Помилка отримання HTML попереднього перегляду:', e);
      return '';
    }
  };



  return (
    <div className="container-fluid p-0">
      {/* Форма зверху - приліплена до верху */}
      <div className="bg-light border-bottom p-3 sticky-top">
        <div className="container">
          <h2 className="mb-3">📄 Генерація PDF рахунку</h2>
          <Row>
            <Col md={3}>
              <label className="form-label fw-bold">{t("chooseDriver")}</label>
              <DriverSelector 
                selectedDriver={selectedDriver} 
                setSelectedDriver={setSelectedDriver} 
              />
            </Col>
            <Col md={3}>
              <label className="form-label fw-bold">{t("selectMonth")}</label>
              <Form.Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">{t("selectMonth")}</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <label className="form-label fw-bold">{t("year")}</label>
              <Form.Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <label className="form-label fw-bold">&nbsp;</label>
              <div>
                <Button 
                  variant="primary" 
                  onClick={() => fetchDriverReports()}
                  disabled={loadingReports || loading}
                  className="w-100"
                >
                  {loadingReports ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t("loading")}...
                    </>
                  ) : (
                    `📊 ${t("loaddata")}`
                  )}
                </Button>
              </div>
            </Col>
          </Row>
          
          {/* Кнопка збереження та завантаження PDF */}
          {invoicePreview?.html && (
            <Row className="mt-3">
              <Col md={12}>
                <div className="d-grid">
                  <Button 
                    variant="success" 
                    onClick={saveAndDownloadInvoice}
                    disabled={generatingInvoice}
                    size="lg"
                  >
                    {generatingInvoice ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Збереження та генерація PDF...
                      </>
                    ) : (
                      '💾 Зберегти і завантажити PDF'
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* Попередній перегляд рахунку в форматі A4 */}

        {/* Спінер під час завантаження */}
        {loadingReports && (
          <div className="container mt-4">
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 fs-5">⏳ Завантаження даних рапортів...</p>
            </div>
          </div>
        )}

        {/* Попередження про відсутність збережених рапортів */}
        {!loadingReports && reportsData && reportsData.reportCount === 0 && selectedDriver && selectedMonth && selectedYear && (
          <div className="container mt-4">
            <Alert variant="warning">
              <Alert.Heading>⚠️ Немає збережених рапортів</Alert.Heading>
              <p>
                Для водія <strong>{selectedDriver.label}</strong> за <strong>{months.find(m => m.value === selectedMonth)?.label} {selectedYear}</strong> немає збережених та опублікованих місячних рапортів.
              </p>
              <hr />
              <p className="mb-0">
                📋 Перейдіть на сторінку <a href="/admin/monthly-reports" className="alert-link">Місячні рапорти</a> для перевірки, редагування та збереження даних за цей місяць.
              </p>
            </Alert>
          </div>
        )}

        {/* Інформація про існуючі рахунки */}
        {!loadingReports && reportsData && reportsData.reportCount > 0 && existingInvoices && existingInvoices.count > 0 && selectedDriver && (
          <div className="container mt-4">
            <Alert variant="info">
              <Alert.Heading>📄 Існуючі рахунки</Alert.Heading>
              <p>
                Для водія <strong>{selectedDriver.label}</strong> за <strong>{months.find(m => m.value === selectedMonth)?.label} {selectedYear}</strong> вже створено <strong>{existingInvoices.count}</strong> рахунків:
              </p>
              <ul className="mb-2">
                {existingInvoices.invoices.map((invoice, index) => (
                  <li key={index}>
                    <strong>Рахунок {invoice.invoice_number}</strong> - {invoice.status === 'draft' ? 'Чернетка' : 'Опубліковано'} 
                    <small className="text-muted ms-2">(створено: {new Date(invoice.created_at).toLocaleDateString('uk-UA')})</small>
                  </li>
                ))}
              </ul>
              <hr />
              <p className="mb-0">
                Ви можете створити новий рахунок або переглянути існуючі на сторінці <a href="/admin/month-summary" className="alert-link">Місячна статистика</a>.
              </p>
            </Alert>
          </div>
        )}

        {/* Попередній перегляд рахунку */}
        {!loadingReports && reportsData && reportsData.reportCount > 0 && selectedDriver && (
          <div className="container mt-4" style={{ overflow: 'hidden' }}>
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>👁️ Попередній перегляд рахунку</h5>
                  {editableInvoices && editableInvoices.length > 0 && (
                    <Button 
                      variant="outline-primary" 
                      onClick={async () => {
                        await fetchLastInvoiceNumber();
                        setShowEditModal(true);
                      }}
                    >
                      ✏️ Редагувати рахунки
                    </Button>
                  )}
                </div>
              
              {/* HTML попередній перегляд в форматі A4 - 80% ширини сторінки */}
              <div 
                className="border shadow-sm mx-auto" 
                style={{
                  width: '80%',
                  backgroundColor: 'white',
                  overflow: 'visible',
                  position: 'relative'
                }}
              >
                <iframe
                  srcDoc={invoicePreview?.html || ''}
                  style={{
                    width: '100%',
                    minHeight: '297mm',
                    border: 'none',
                    display: 'block',
                    overflow: 'visible'
                  }}
                  scrolling="no"
                  title="Invoice Preview"
                  onLoad={(e) => {
                    // Автоматично підлаштовуємо висоту iframe під контент
                    try {
                      const iframe = e.target;
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                      if (iframeDoc.body) {
                        const height = Math.max(
                          iframeDoc.body.scrollHeight,
                          iframeDoc.body.offsetHeight,
                          iframeDoc.documentElement.clientHeight,
                          iframeDoc.documentElement.scrollHeight,
                          iframeDoc.documentElement.offsetHeight
                        );
                        iframe.style.height = height + 'px';
                      }
                    } catch (err) {
                      console.warn('Не вдалося автоматично налаштувати висоту iframe:', err);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Завантаження даних */}
      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" />
          <p className="mt-2">{t("loading")}...</p>
        </div>
      )}

      {/* Помилка валідації */}
      {validationError && (
        <Alert variant="warning" className="mt-4">
          <Alert.Heading>⚠️ Увага!</Alert.Heading>
          {validationError}
        </Alert>
      )}

      {/* Помилка завантаження */}
      {error && (
        <Alert variant="danger" className="mt-4">
          <Alert.Heading>Помилка!</Alert.Heading>
          {error}
          <hr />
          <button className="btn btn-outline-danger" onClick={fetchMonthsData}>
            Спробувати ще раз
          </button>
        </Alert>
      )}


      {/* Повідомлення про вибір місяця */}
      {!loading && !error && monthsData && (!selectedMonth || !selectedYear) && (
        <Alert variant="info" className="mt-4">
          <Alert.Heading>Виберіть місяць та рік</Alert.Heading>
          Для перегляду тижнів виберіть місяць та рік у формах вище.
        </Alert>
      )}

      {/* Модальне вікно для редагування рахунків */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)} 
        size="xl"
        scrollable={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>✏️ Редагування рахунків</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Інформація про останній номер рахунку */}
          <Alert variant="info" className="mb-3">
            <strong>Останній номер рахунку за місяць:</strong> {lastInvoiceNumber} 
            <br />
            <small className="text-muted">Наступні вільні номери: {lastInvoiceNumber + 1}, {lastInvoiceNumber + 2}</small>
          </Alert>

          {editableInvoices && editableInvoices.length > 0 ? (
            editableInvoices.map((invoice, invoiceIndex) => (
              <div key={invoiceIndex} className="mb-4 border p-3 rounded">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <span className="badge bg-secondary me-2">
                      {invoice.payment_method === 'transfer' ? 'Переказ' : 'Готівка'}
                    </span>
                  </h6>
                </div>
                
                {/* Поле для номера рахунку */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Номер рахунку:</label>
                  <Form.Control
                    type="text"
                    value={invoice.invoice_number || ''}
                    onChange={(e) => updateInvoiceNumber(invoiceIndex, e.target.value)}
                    placeholder="Введіть номер рахунку"
                  />
                </div>
                
                {/* Поле для опису рахунку */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Опис послуги:</label>
                  <Form.Control
                    type="text"
                    value={invoice.description || 'Wynajem samochodu'}
                    onChange={(e) => updateInvoiceDescription(invoiceIndex, e.target.value)}
                    placeholder="Введіть опис послуги"
                  />
                </div>

                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '30%' }}>Тиждень</th>
                      <th style={{ width: '40%' }}>Сума (zł)</th>
                      <th style={{ width: '20%' }}>Тип</th>
                      <th style={{ width: '10%' }}>Дії</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.weeks && invoice.weeks.length > 0 ? (
                      invoice.weeks.map((week, weekIndex) => (
                        <tr key={weekIndex}>
                          <td>
                            <Form.Control
                              type="text"
                              value={week.week || ''}
                              onChange={(e) => updateWeekField(invoiceIndex, weekIndex, 'week', e.target.value)}
                              placeholder="Номер тижня"
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              step="0.01"
                              value={week.amount || 0}
                              onChange={(e) => updateWeekAmount(invoiceIndex, weekIndex, e.target.value)}
                            />
                          </td>
                          <td>
                            <Form.Select
                              value={week.type || 'payout'}
                              onChange={(e) => updateWeekField(invoiceIndex, weekIndex, 'type', e.target.value)}
                              size="sm"
                            >
                              <option value="payout">Payout</option>
                              <option value="cash">Cash</option>
                            </Form.Select>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeWeekRow(invoiceIndex, weekIndex)}
                            >
                              🗑️
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          Немає даних про тижні
                        </td>
                      </tr>
                    )}
                    <tr className="table-info">
                      <td colSpan="2"><strong>Всього:</strong></td>
                      <td>
                        <strong>
                          {invoice.weeks 
                            ? invoice.weeks.reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0).toFixed(2)
                            : '0.00'} zł
                        </strong>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => addWeekRow(invoiceIndex)}
                        >
                          ➕ Додати
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            ))
          ) : (
            <Alert variant="info">Немає рахунків для редагування</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Скасувати
          </Button>
          <Button 
            variant="primary" 
            onClick={saveInvoiceEdits}
            disabled={savingEdits || !editableInvoices}
          >
            {savingEdits ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Збереження...
              </>
            ) : (
              '💾 Зберегти зміни'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GenerateInvoice;
